import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import {
  appApi,
  useGetDailyRosterQuery,
  useGetMyCalendarQuery,
  useGetAllProjectsForMatrixQuery,
} from "../../../../store/appApi";
import { selectUser, selectUserRole } from "../../../../store/authSlice";
import { adminCreateLeaveApi, adminUpdateLeaveApi } from "../../../../services/leave";
import { adminManualAttendanceApi } from "../../../../services/attendance";
import PageTitle from "../../../layouts/PageTitle";
import { formatWorkDuration } from "./attendanceUtils";
import ManualEntryNote, { ManualMark } from "./ManualEntryNote";

const LEAVE_TYPES = [
  "Casual Leave",
  "Sick Leave",
  "Earned Leave",
  "Maternity Leave",
  "Paternity Leave",
  "Unpaid Leave",
];
const LEAVE_PERIODS = ["Full Day", "First Half", "Second Half"];

// Local-calendar "YYYY-MM-DD" — never round-trips through ISO/UTC, so the day
// the admin picked can't shift on the wire (see the same helper in Apply.jsx).
const toDateOnlyString = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// ─── Admin: mark leave on an employee's behalf ──────────────────────────────
// `existingLeave` (from the roster row) switches this into edit mode — every
// date field, including back dates, is freely editable either way; nothing
// here restricts how far in the past leaveFrom/leaveTo can be.
const MarkLeaveModal = ({ employee, defaultDate, existingLeave, onClose, onDone }) => {
  const isEdit = !!existingLeave?.leaveId;
  const [form, setForm] = useState({
    leaveType: existingLeave?.leaveType || "Casual Leave",
    leaveFrom: existingLeave?.leaveFrom ? new Date(existingLeave.leaveFrom) : defaultDate,
    leaveTo: existingLeave?.leaveTo ? new Date(existingLeave.leaveTo) : defaultDate,
    leavePeriod: existingLeave?.leavePeriod || "Full Day",
    adminRemarks: existingLeave?.leaveAdminRemarks || "",
    desc: existingLeave?.leaveDesc || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.leaveFrom || !form.leaveTo || form.leaveTo < form.leaveFrom) {
      Swal.fire("Error", "Select a valid date range.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        leaveType: form.leaveType,
        leaveFrom: toDateOnlyString(form.leaveFrom),
        leaveTo: toDateOnlyString(form.leaveTo),
        leavePeriod: form.leavePeriod,
        adminRemarks: form.adminRemarks,
        desc: form.desc,
      };
      if (isEdit) {
        await adminUpdateLeaveApi(existingLeave.leaveId, payload);
        Swal.fire("Updated", `Leave corrected for ${employee.name}.`, "success");
      } else {
        await adminCreateLeaveApi({
          userId: employee.userId || employee.employeeId,
          ...payload,
        });
        Swal.fire("Marked", `Leave recorded for ${employee.name}.`, "success");
      }
      onDone();
      onClose();
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message ||
          (isEdit ? "Could not update leave." : "Could not mark leave."),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: 420, maxWidth: "94vw", marginBottom: 0 }}
      >
        <div className="card-header d-flex align-items-center justify-content-between">
          <h5 className="mb-0">
            {isEdit ? "Edit Leave" : "Mark Leave"} — {employee.name}
          </h5>
          <button className="btn btn-xs btn-outline-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="card-body">
          <div className="mb-2">
            <label className="form-label mb-1">Leave Type</label>
            <select
              className="form-control form-control-sm"
              value={form.leaveType}
              onChange={(e) =>
                setForm((f) => ({ ...f, leaveType: e.target.value }))
              }
            >
              {LEAVE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-6">
              <label className="form-label mb-1">From</label>
              <DatePicker
                selected={form.leaveFrom}
                onChange={(d) =>
                  setForm((f) => ({
                    ...f,
                    leaveFrom: d,
                    leaveTo: f.leaveTo && f.leaveTo < d ? d : f.leaveTo,
                  }))
                }
                dateFormat="dd MMM yyyy"
                className="form-control form-control-sm"
              />
            </div>
            <div className="col-6">
              <label className="form-label mb-1">To</label>
              <DatePicker
                selected={form.leaveTo}
                onChange={(d) => setForm((f) => ({ ...f, leaveTo: d }))}
                minDate={form.leaveFrom}
                dateFormat="dd MMM yyyy"
                className="form-control form-control-sm"
              />
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label mb-1">Period</label>
            <select
              className="form-control form-control-sm"
              value={form.leavePeriod}
              onChange={(e) =>
                setForm((f) => ({ ...f, leavePeriod: e.target.value }))
              }
            >
              {LEAVE_PERIODS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-2">
            <label className="form-label mb-1">Remarks (admin note)</label>
            <textarea
              className="form-control form-control-sm"
              rows={2}
              value={form.adminRemarks}
              onChange={(e) =>
                setForm((f) => ({ ...f, adminRemarks: e.target.value }))
              }
              placeholder="Why this leave is being recorded…"
            />
          </div>
          <button
            className="btn btn-primary btn-sm w-100"
            disabled={saving}
            onClick={submit}
          >
            {saving
              ? "Saving…"
              : isEdit
                ? "Save changes"
                : "Mark Leave (auto-approved)"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Super-admin: record / correct a day's attendance by hand (no selfie) ───
// Remarks are mandatory — they are saved with the record and shown on the
// employee's calendar.
const toTimeInput = (value) =>
  value
    ? new Date(value).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "";

const ManualAttendanceModal = ({ employee, date, onClose, onDone }) => {
  const [punchIn, setPunchIn] = useState(toTimeInput(employee.punchIn) || "09:30");
  const [punchOut, setPunchOut] = useState(toTimeInput(employee.punchOut));
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!/^\d{1,2}:\d{2}$/.test(punchIn)) {
      Swal.fire("Error", "Enter a valid punch-in time (HH:MM).", "error");
      return;
    }
    if (punchOut && punchOut <= punchIn) {
      Swal.fire("Error", "Punch-out must be after punch-in.", "error");
      return;
    }
    if (!remarks.trim()) {
      Swal.fire(
        "Remarks required",
        "Say why this attendance is being entered manually.",
        "error",
      );
      return;
    }
    setSaving(true);
    try {
      await adminManualAttendanceApi({
        employeeId: employee.userId || employee.employeeId,
        date,
        punchIn,
        ...(punchOut ? { punchOut } : {}),
        remarks: remarks.trim(),
      });
      Swal.fire(
        "Saved",
        `Attendance for ${employee.name} on ${date} recorded manually.`,
        "success",
      );
      onDone();
      onClose();
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || "Could not save attendance.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: 400, maxWidth: "94vw", marginBottom: 0 }}
      >
        <div className="card-header d-flex align-items-center justify-content-between">
          <h5 className="mb-0">Manual Attendance — {employee.name}</h5>
          <button className="btn btn-xs btn-outline-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="card-body">
          <p className="text-muted small mb-2">
            {date} · no selfie needed
          </p>
          <div className="row g-2">
            <div className="col-6">
              <label className="form-label mb-1">Punch-in</label>
              <input
                type="time"
                step="60"
                className="form-control form-control-sm"
                value={punchIn}
                onChange={(e) => setPunchIn(e.target.value)}
              />
            </div>
            <div className="col-6">
              <label className="form-label mb-1">Punch-out (optional)</label>
              <input
                type="time"
                step="60"
                className="form-control form-control-sm"
                value={punchOut}
                onChange={(e) => setPunchOut(e.target.value)}
              />
            </div>
          </div>
          <p className="text-muted small mt-2 mb-2">
            A punch-in after 9:35 is treated as a half day. Hours are counted
            up to 18:30.
          </p>
          <label className="form-label mb-1">
            Remarks <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control form-control-sm"
            rows={3}
            maxLength={500}
            placeholder="Why is this attendance being entered manually?"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
          {employee.manualEntry && (
            <p className="text-muted small mt-2 mb-0">
              Previous remarks: {employee.manualEntry.remarks}
            </p>
          )}
          <button
            className="btn btn-primary btn-sm w-100 mt-3"
            disabled={saving}
            onClick={submit}
          >
            {saving ? "Saving…" : "Save attendance"}
          </button>
        </div>
      </div>
    </div>
  );
};

const STATUS_BADGE = {
  present: "success",
  "half-day": "warning",
  "half-day-leave": "warning",
  "half-day-present": "warning",
  partial: "warning",
  on_leave: "info",
  pending: "warning",
  absent: "danger",
  upcoming: "light",
};

const COLOR_MAP = {
  PRESENT: "#2bc155",
  PARTIAL: "#f6a821",
  ON_LEAVE: "#3f9ae0",
  ON_LEAVE_FIRST_HALF: "#0ea5e9",
  ON_LEAVE_SECOND_HALF: "#0284c7",
  HALF_DAY_LEAVE: "#3f9ae0",
  HALF_DAY_PRESENT: "#f59e0b",
  PENDING: "#ff9f43",
  PENDING_FIRST_HALF: "#ff9f43",
  PENDING_SECOND_HALF: "#ff9f43",
  ABSENT: "#f32d2d",
  HOLIDAY: "#8b5cf6",
  UPCOMING: "#64748b",
};

const PRESENT_COLOR = "#2bc155";

const TAG_LABEL = {
  PRESENT: "Present",
  PARTIAL: "Partial",
  ON_LEAVE: "On Leave",
  ON_LEAVE_FIRST_HALF: "On Leave (First Half)",
  ON_LEAVE_SECOND_HALF: "On Leave (Second Half)",
  HALF_DAY_LEAVE: "Half Day Leave",
  HALF_DAY_PRESENT: "First Half Day",
  PENDING: "Leave Pending",
  PENDING_FIRST_HALF: "Leave Pending (First Half)",
  PENDING_SECOND_HALF: "Leave Pending (Second Half)",
  ABSENT: "Absent",
  HOLIDAY: "Holiday",
  UPCOMING: "Upcoming",
};

const todayStr = () => new Date().toISOString().split("T")[0];

const toMonthStr = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${m}`;
};

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const SelfieThumb = ({ url, label }) => {
  const [open, setOpen] = useState(false);
  if (!url)
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 40,
          height: 40,
          borderRadius: 8,
          border: "1px dashed #dee2e6",
          color: "#adb5bd",
          fontSize: 14,
        }}
      >
        —
      </span>
    );
  const src = url.startsWith("http") ? url : `${BASE_URL}${url}`;
  return (
    <>
      <img
        src={src}
        alt={label}
        onClick={() => setOpen(true)}
        style={{
          width: 40,
          height: 40,
          objectFit: "cover",
          borderRadius: 8,
          cursor: "pointer",
          border: "1px solid #dee2e6",
          display: "block",
        }}
        title={`Click to enlarge ${label}`}
      />
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
        >
          <div style={{ position: "relative" }}>
            <img
              src={src}
              alt={label}
              style={{ maxHeight: "80vh", maxWidth: "90vw", borderRadius: 8 }}
            />
            <div
              style={{
                textAlign: "center",
                color: "#fff",
                marginTop: 8,
                fontSize: 13,
              }}
            >
              {label}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Clickable punch-location chip — red when the punch happened outside the
// admin-configured geofence, green when inside. Navigates to a map view of
// exactly where it was recorded. Rendered as a fixed-width pill so the "In"
// and "Out" chips stack in a tidy column.
const LocationTag = ({
  label,
  lat,
  lng,
  withinGeofence,
  address,
  time,
  employeeName,
  navigate,
}) => {
  const missing = lat == null || lng == null;
  const outside = withinGeofence === false;

  if (missing) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 62,
          padding: "3px 8px",
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1.4,
          color: "#adb5bd",
          border: "1px dashed #dee2e6",
        }}
      >
        {label} —
      </span>
    );
  }

  return (
    <span
      role="button"
      title={
        outside
          ? "Outside work location — click to view on map"
          : "Click to view on map"
      }
      onClick={() =>
        navigate("/staff/attendance/location", {
          state: { employeeName, label, lat, lng, withinGeofence, address, time },
        })
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        minWidth: 62,
        padding: "3px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.4,
        cursor: "pointer",
        color: "#fff",
        backgroundColor: outside ? "#f32d2d" : "#2bc155",
      }}
    >
      <i className="fas fa-map-marker-alt" style={{ fontSize: 10 }} />
      {label}
    </span>
  );
};

// ─── Admin view: full employee roster ────────────────────────────────────────
const RosterTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [search, setSearch] = useState("");
  const [filterProjectId, setFilterProjectId] = useState("");
  const [markLeaveFor, setMarkLeaveFor] = useState(null);
  const [editPunchFor, setEditPunchFor] = useState(null);

  // Month/year quick-jump
  const [jumpYear, setJumpYear] = useState(new Date().getFullYear());
  const [jumpMonth, setJumpMonth] = useState(new Date().getMonth() + 1);

  const date = selectedDate.toISOString().split("T")[0];
  const { data, isLoading } = useGetDailyRosterQuery({
    date,
    projectId: filterProjectId || undefined,
  });
  const { data: projectsData } = useGetAllProjectsForMatrixQuery();
  const projects = projectsData?.data || [];

  const viewerRole = useSelector(selectUserRole);
  const roster = (data?.roster || [])
    .filter((e) => e.name?.toLowerCase().includes(search.toLowerCase()))
    // A super-admin viewing the roster doesn't need to see super-admin
    // accounts (themselves included) — they never punch attendance.
    .filter((e) => !(viewerRole === "super-admin" && e.role === "super-admin"));

  const applyMonthJump = () => {
    const day = selectedDate.getDate();
    const daysInMonth = new Date(jumpYear, jumpMonth, 0).getDate();
    const clampedDay = Math.min(day, daysInMonth);
    setSelectedDate(new Date(jumpYear, jumpMonth - 1, clampedDay));
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <>
      <PageTitle activeMenu="Daily Roster" motherMenu="Attendance" />
      <div className="card">
        <div className="card-header d-flex flex-wrap gap-2 align-items-center">
          <h4 className="card-title mb-0 me-auto">Daily Roster</h4>

          {/* Month/Year quick jump */}
          <div className="d-flex align-items-center gap-1">
            <select
              className="form-select form-select-sm"
              style={{ width: 110 }}
              value={jumpMonth}
              onChange={(e) => setJumpMonth(Number(e.target.value))}
            >
              {months.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              style={{ width: 85 }}
              value={jumpYear}
              onChange={(e) => setJumpYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={applyMonthJump}
            >
              Go
            </button>
          </div>

          <DatePicker
            selected={selectedDate}
            onChange={(d) => d && setSelectedDate(d)}
            dateFormat="dd MMM yyyy"
            maxDate={new Date()}
            className="form-control form-control-sm"
            style={{ width: 160 }}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
          />
          <select
            className="form-select form-select-sm"
            style={{ width: 180 }}
            value={filterProjectId}
            onChange={(e) => setFilterProjectId(e.target.value)}
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>
                {p.projectName}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: 180 }}
            placeholder="Search employee…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-4">Loading…</div>
          ) : roster.length === 0 ? (
            <div className="text-center text-muted py-4">No records found.</div>
          ) : (
            <div className="table-responsive roster-table">
              <style>{`
                .roster-table table { margin-bottom: 0; }
                .roster-table thead th {
                  text-transform: uppercase;
                  font-size: 11px;
                  letter-spacing: 0.4px;
                  font-weight: 700;
                  color: #74788d;
                  border-bottom: 1px solid #e6e6e6;
                  white-space: nowrap;
                  padding: 12px 14px;
                }
                .roster-table tbody td {
                  padding: 12px 14px;
                  vertical-align: middle;
                  border-bottom: 1px solid #f0f0f0;
                }
                .roster-table tbody tr:last-child td { border-bottom: 0; }
                .roster-table .cell-name {
                  font-weight: 600;
                  text-transform: capitalize;
                  color: #2b2b40;
                  white-space: nowrap;
                }
                .roster-table .cell-time {
                  white-space: nowrap;
                  font-variant-numeric: tabular-nums;
                  color: #3e4954;
                }
                .roster-table .cell-time.is-empty { color: #adb5bd; }
                .roster-table .cell-hours {
                  white-space: nowrap;
                  font-weight: 600;
                  font-variant-numeric: tabular-nums;
                }
                .roster-table .status-badge {
                  display: inline-block;
                  padding: 4px 10px;
                  border-radius: 999px;
                  font-size: 11px;
                  font-weight: 700;
                  letter-spacing: 0.3px;
                  text-transform: uppercase;
                }
              `}</style>
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Punch In</th>
                    <th>In Selfie</th>
                    <th>Punch Out</th>
                    <th>Out Selfie</th>
                    <th>Work Hours</th>
                    <th>Location</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {roster.map((emp) => {
                    const duration = formatWorkDuration(
                      emp.workDurationMinutes ?? emp.workHours,
                    );
                    return (
                      <tr key={emp.userId || emp.employeeId}>
                        <td className="cell-name">{emp.name}</td>
                        <td style={{ whiteSpace: "nowrap", color: "#3e4954" }}>
                          {emp.role}
                        </td>
                        <td>
                          <span
                            className="badge status-badge"
                            style={{
                              // Daily roster returns `tag` + `color` (from the
                              // backend's classifyDay) — there is no `status`
                              // field here, which is why every badge was falling
                              // back to grey.
                              backgroundColor:
                                emp.color ||
                                COLOR_MAP[emp.tag] ||
                                COLOR_MAP[emp.status] ||
                                "#74788d",
                              color: "#fff",
                            }}
                          >
                            {emp.tag === "HOLIDAY" && emp.holiday
                              ? `Holiday · ${emp.holiday}`
                              : (emp.status || emp.tag || "").replace(/_/g, " ")}
                          </span>
                          {emp.shortLeave && (
                            <span
                              className="badge d-block mt-1"
                              style={{ background: "#8b5cf6", color: "#fff" }}
                              title={
                                emp.shortLeave.from && emp.shortLeave.to
                                  ? `Short leave ${emp.shortLeave.from}–${emp.shortLeave.to}`
                                  : "Short leave"
                              }
                            >
                              ⏱ Short leave{" "}
                              {emp.shortLeave.hours ? `${emp.shortLeave.hours}h` : ""}
                            </span>
                          )}
                          {emp.manualEntry && (
                            <span
                              className="badge d-block mt-1"
                              style={{ background: "#b45309", color: "#fff" }}
                              title={`${emp.manualEntry.remarks}${
                                emp.manualEntry.markedBy
                                  ? ` — ${emp.manualEntry.markedBy}`
                                  : ""
                              }`}
                            >
                              ✎ Manual
                            </span>
                          )}
                          {duration && (
                            <small className="d-block text-muted mt-1">
                              {duration}
                            </small>
                          )}
                        </td>
                        <td
                          className={`cell-time${emp.punchIn ? "" : " is-empty"}`}
                        >
                          {emp.punchIn
                            ? new Date(emp.punchIn).toLocaleTimeString()
                            : "—"}
                        </td>
                        <td>
                          <SelfieThumb
                            url={emp.selfieUrl}
                            label="Punch-In Selfie"
                          />
                        </td>
                        <td
                          className={`cell-time${emp.punchOut ? "" : " is-empty"}`}
                        >
                          {emp.punchOut
                            ? new Date(emp.punchOut).toLocaleTimeString()
                            : "—"}
                        </td>
                        <td>
                          <SelfieThumb
                            url={emp.punchOutSelfieUrl}
                            label="Punch-Out Selfie"
                          />
                        </td>
                        <td
                          className={`cell-hours${duration ? "" : " text-muted"}`}
                        >
                          {duration || "—"}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "inline-flex",
                              flexDirection: "column",
                              gap: 4,
                            }}
                          >
                            <LocationTag
                              label="In"
                              lat={emp.punchInLat}
                              lng={emp.punchInLng}
                              withinGeofence={emp.punchInWithinGeofence}
                              address={emp.punchInAddress}
                              time={emp.punchIn}
                              employeeName={emp.name}
                              navigate={navigate}
                            />
                            <LocationTag
                              label="Out"
                              lat={emp.punchOutLat}
                              lng={emp.punchOutLng}
                              withinGeofence={emp.punchOutWithinGeofence}
                              address={emp.punchOutAddress}
                              time={emp.punchOut}
                              employeeName={emp.name}
                              navigate={navigate}
                            />
                          </div>
                        </td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <div className="d-inline-flex flex-column gap-1">
                            <button
                              className="btn btn-xs btn-outline-primary"
                              onClick={() =>
                                navigate(
                                  `/staff/attendance/employee/${emp.userId || emp.employeeId}`,
                                )
                              }
                            >
                              View
                            </button>
                            <button
                              className="btn btn-xs btn-outline-info"
                              onClick={() => setMarkLeaveFor(emp)}
                            >
                              {emp.leaveId ? "Edit Leave" : "Mark Leave"}
                            </button>
                            <button
                              className="btn btn-xs btn-outline-secondary"
                              onClick={() => setEditPunchFor(emp)}
                            >
                              Manual Entry
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {markLeaveFor && (
        <MarkLeaveModal
          employee={markLeaveFor}
          defaultDate={selectedDate}
          existingLeave={markLeaveFor.leaveId ? markLeaveFor : null}
          onClose={() => setMarkLeaveFor(null)}
          onDone={() =>
            dispatch(
              appApi.util.invalidateTags([
                "AttendanceRoster",
                "AttendanceCalendar",
                "Leaves",
              ]),
            )
          }
        />
      )}

      {editPunchFor && (
        <ManualAttendanceModal
          employee={editPunchFor}
          date={date}
          onClose={() => setEditPunchFor(null)}
          onDone={() =>
            dispatch(
              appApi.util.invalidateTags([
                "AttendanceRoster",
                "AttendanceCalendar",
              ]),
            )
          }
        />
      )}
    </>
  );
};

// ─── Non-admin view: logged-in user's own month calendar ─────────────────────
const MyAttendanceView = () => {
  const user = useSelector(selectUser);
  const [monthDate, setMonthDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  const month = toMonthStr(monthDate);
  const { data, isLoading } = useGetMyCalendarQuery(month);

  const days = data?.days || [];
  const summary = data?.summary || {};

  const firstDay = new Date(
    Date.UTC(monthDate.getFullYear(), monthDate.getMonth(), 1),
  ).getUTCDay();
  const blanks = Array(firstDay).fill(null);

  const sel = selectedDay && days.find((d) => d.date === selectedDay);

  return (
    <>
      <PageTitle activeMenu="My Attendance" motherMenu="Attendance" />

      {/* Stats */}
      <div className="row g-2 mb-3">
        {[
          { label: "Present", key: "present", color: "#2bc155" },
          { label: "Partial", key: "partial", color: "#f6a821" },
          { label: "First Half Day", key: "halfDayPresent", color: "#f59e0b" },
          { label: "Half Day Leave", key: "halfDay", color: "#3f9ae0" },
          { label: "On Leave", key: "onLeave", color: "#3f9ae0" },
          { label: "Absent", key: "absent", color: "#f32d2d" },
        ].map((s) => (
          <div className="col-6 col-sm-3" key={s.key}>
            <div
              className="card text-center py-2"
              style={{ borderLeft: `4px solid ${s.color}` }}
            >
              <h4 className="mb-0">{summary[s.key] ?? 0}</h4>
              <small className="text-muted">{s.label}</small>
            </div>
          </div>
        ))}
      </div>

      <div className="row">
        <div className={sel ? "col-xl-8" : "col-12"}>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  const d = new Date(monthDate);
                  d.setMonth(d.getMonth() - 1);
                  setMonthDate(d);
                  setSelectedDay(null);
                }}
              >
                &lsaquo;
              </button>
              <div>
                <h5 className="mb-0 d-inline">
                  {monthDate.toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                  })}
                </h5>
                {user?.name && (
                  <span className="ms-2 text-muted small">— {user.name}</span>
                )}
              </div>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  const d = new Date(monthDate);
                  d.setMonth(d.getMonth() + 1);
                  setMonthDate(d);
                  setSelectedDay(null);
                }}
              >
                &rsaquo;
              </button>
            </div>
            <div className="card-body p-2">
              {isLoading ? (
                <div className="text-center py-4">Loading…</div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7,1fr)",
                    gap: 4,
                  }}
                >
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (d) => (
                      <div
                        key={d}
                        className="text-center fw-bold small text-muted py-1"
                      >
                        {d}
                      </div>
                    ),
                  )}
                  {blanks.map((_, i) => (
                    <div key={`b${i}`} />
                  ))}
                  {days.map((day) => {
                    const isHalfDay = day.tag === "HALF_DAY_LEAVE";
                    const dayColor = COLOR_MAP[day.tag] || "#ccc";
                    return (
                      <div
                        key={day.date}
                        onClick={() =>
                          setSelectedDay(
                            day.date === selectedDay ? null : day.date,
                          )
                        }
                        style={{
                          // Split diagonal gradient for half-day: left = leave teal, right = present green
                          background: isHalfDay
                            ? `linear-gradient(135deg, ${dayColor}33 50%, ${PRESENT_COLOR}33 50%)`
                            : `${dayColor}22`,
                          borderLeft: `3px solid ${dayColor}`,
                          borderRight: isHalfDay
                            ? `3px solid ${PRESENT_COLOR}`
                            : undefined,
                          borderRadius: 4,
                          padding: "5px 4px",
                          cursor: "pointer",
                          outline:
                            selectedDay === day.date
                              ? `2px solid ${dayColor}`
                              : "none",
                          minHeight: 52,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div className="fw-bold" style={{ fontSize: 13 }}>
                          {parseInt(day.date.split("-")[2])}
                          {day.shortLeave && (
                            <span
                              title="Short leave"
                              style={{ marginLeft: 2, fontSize: 8, color: "#8b5cf6" }}
                            >
                              ⏱{day.shortLeave.hours ? day.shortLeave.hours + "h" : ""}
                            </span>
                          )}
                          <ManualMark entry={day.manualEntry} fontSize={8} />
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 600,
                            color: dayColor,
                            textAlign: "center",
                            lineHeight: 1.1,
                            marginTop: 2,
                          }}
                        >
                          {day.holiday || TAG_LABEL[day.tag]}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="card-footer d-flex flex-wrap gap-2">
              {Object.entries(COLOR_MAP).map(([tag, color]) => (
                <span
                  key={tag}
                  className="d-flex align-items-center gap-1 small"
                >
                  {tag === "HALF_DAY_LEAVE" ? (
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        display: "inline-block",
                        background: `linear-gradient(135deg, ${color} 50%, ${PRESENT_COLOR} 50%)`,
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        background: color,
                        borderRadius: 2,
                        display: "inline-block",
                      }}
                    />
                  )}
                  {TAG_LABEL[tag]}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Day detail */}
        {sel && (
          <div className="col-xl-4">
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between">
                <h6 className="mb-0">{sel.date}</h6>
                <button
                  className="btn btn-xs btn-outline-secondary"
                  onClick={() => setSelectedDay(null)}
                >
                  ✕
                </button>
              </div>
              <div className="card-body">
                <span
                  className="badge mb-2"
                  style={{ background: COLOR_MAP[sel.tag] }}
                >
                  {sel.holiday ? `Holiday — ${sel.holiday}` : TAG_LABEL[sel.tag]}
                </span>
                {sel.punchIn && (
                  <p className="mb-1">
                    <strong>Punch In:</strong>{" "}
                    {new Date(sel.punchIn).toLocaleTimeString()}
                  </p>
                )}
                {sel.punchOut && (
                  <p className="mb-1">
                    <strong>Punch Out:</strong>{" "}
                    {new Date(sel.punchOut).toLocaleTimeString()}
                  </p>
                )}
                {sel.workDurationMinutes != null && (
                  <p className="mb-1">
                    <strong>Work Hours:</strong>{" "}
                    {formatWorkDuration(sel.workDurationMinutes)}
                  </p>
                )}
                {sel.distanceFromSite != null && (
                  <p className="mb-1">
                    <strong>Distance from site:</strong> {sel.distanceFromSite}m
                  </p>
                )}
                {sel.leave && (
                  <p className="mb-1">
                    <strong>Leave:</strong> {sel.leave.leaveType}{" "}
                    <span
                      className={`badge bg-${STATUS_BADGE[sel.leave.status] || "secondary"}`}
                    >
                      {sel.leave.status}
                    </span>
                  </p>
                )}
                {sel.shortLeave && (
                  <p className="mb-1">
                    <strong>Short leave:</strong>{" "}
                    {sel.shortLeave.from && sel.shortLeave.to
                      ? `${sel.shortLeave.from}–${sel.shortLeave.to}`
                      : ""}
                    {sel.shortLeave.hours ? ` · ${sel.shortLeave.hours}h` : ""}{" "}
                    <span className="badge" style={{ background: "#8b5cf6" }}>
                      intimation
                    </span>
                  </p>
                )}
                {/* Punch-in selfie */}
                {sel.selfieUrl && (
                  <div className="mt-2">
                    <p className="mb-1 fw-semibold small">Punch-In Selfie</p>
                    <SelfieThumb url={sel.selfieUrl} label="Punch-In Selfie" />
                  </div>
                )}
                {/* Punch-out selfie */}
                {sel.punchOutSelfieUrl && (
                  <div className="mt-2">
                    <p className="mb-1 fw-semibold small">Punch-Out Selfie</p>
                    <SelfieThumb
                      url={sel.punchOutSelfieUrl}
                      label="Punch-Out Selfie"
                    />
                  </div>
                )}
                <ManualEntryNote entry={sel.manualEntry} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ─── Router: pick view based on role ─────────────────────────────────────────
const AdminRoster = () => {
  const role = useSelector(selectUserRole);
  const isSuperAdmin = role === "super-admin";
  return isSuperAdmin ? <RosterTable /> : <MyAttendanceView />;
};

export default AdminRoster;
